/** @jsxImportSource @emotion/react */
import { useLocation } from '@reach/router';
import { useCart } from '@shared/cart-react';
import { SmoothMount, TextInput } from '@shared/react-ui-core';
import useEmotive from '@website/hooks/useEmotive';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmail } from 'validator';
import * as Yup from 'yup';
import { localStore } from '../../libs/storage';
import { SeoFragmentPage } from '../seo';
import analytics from '@website/utils/analytics';
import { Button, cn, Icon } from '@shared/cove-ui';

interface PromoBannerProps {
  active?: boolean;
  className?: string;
  data?: SeoFragmentPage;
}

const PromoBanner: React.FC<PromoBannerProps> = ({
  active = true,
  className,
  data,
}) => {
  const { edgetag } = useEmotive();

  const cart = useCart();
  const [status, setStatus] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    const status = JSON.parse(localStore.getItem('bannerSubscribed'));
    if (status) {
      setStatus('done');
    }
  }, []);

  const handleSubscribe = (email: string) => {
    const partnerId = localStore.getItem('partnerId');
    setEmailError('');

    cart
      .getBannerOffer(email)
      .then((res) => {
        const { data, error } = res;
        if (error || !data) {
          setStatus('error');
          let message = 'Failed to send email';
          if ((error as { message?: string }).message === 'ValidationError') {
            message = 'Invalid email';
          }
          setEmailError(message);
          return;
        }
        setStatus('done');
        edgetag.trackCustomerData({ email });
        edgetag.trackSubscribe();
        analytics.identify({
          email,
          cartId: cart.cartId,
        });
        analytics.track('sign_up', {
          email,
          cartId: cart.cartId,
          ecommerce: {
            method: partnerId,
          },
        });
      })
      .catch((err) => {
        if (
          err?.response?.status === 400 &&
          err?.response?.data?.source === 'Invalid Email'
        ) {
          const message = 'Unable to verify email';
          setStatus('unverifiedEmail');
          setEmailError(message);
          setTimeout(() => setEmailError(''), 3000);
          return;
        }
        setStatus('error');
      });
  };

  const emailIsValid = (email: string) => {
    setStatus('submitting');

    if (isEmail(email)) {
      handleSubscribe(email);
    } else {
      setStatus('invalid');
    }
  };

  return (
    <PromoBannerVariant
      data={data}
      status={status}
      error={emailError}
      handleSubscribe={(email) => emailIsValid(email)}
      active={active}
      className={className}
    />
  );
};

const PromoBannerVariant: React.FC<{
  data?: SeoFragmentPage;
  status: string;
  error?: string;
  handleSubscribe: (email: string) => void;
  active?: boolean;
  className?: string;
}> = ({ data, status, error, handleSubscribe, active, className }) => {
  const {
    promoBannerTitle,
    promoBannerBoldText,
    promoBannerText,
    promoBannerSaleName,
  } = data?.datoCmsSiteMetaInfo || {
    promoBannerTitle: '70% Off Equipment + Free HD Camera',
    promoBannerBoldText: 'Ends Soon',
    promoBannerText: 'Valid on any new system',
    promoBannerSaleName: '',
  };

  const location = useLocation();
  const { t } = useTranslation('common');
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if banner was previously dismissed
    const isDismissed = JSON.parse(
      localStore.getItem('bannerDismissed') || 'false',
    );
    if (!isDismissed) {
      setShowBanner(true);
    }
  }, []);

  return (
    <SmoothMount show={showBanner}>
      <div
        className={cn(
          'relative w-full bg-cove-green',
          !active && 'sr-only',
          className,
        )}
      >
        <Button
          onClick={() => {
            setShowBanner(false);
            localStore.setItem('bannerDismissed', 'true');
          }}
          variant="unstyled"
          className="absolute right-0 top-0"
          aria-label="Close promo banner button"
        >
          <Icon icon="Cancel" className="text-cove-navy-blue" />
        </Button>
        <div className="mx-auto my-0 flex w-full max-w-[1440px] flex-col items-start justify-between px-cove-15 py-cove-10 sm:items-center sm:gap-cove-5 sm:px-cove-25 sm:py-cove-15 md:flex-row md:gap-cove-10 md:p-cove-25 lg:px-cove-50">
          <div className="flex flex-row items-center justify-start gap-cove-10 sm:justify-center md:flex-col md:items-start md:justify-start md:gap-0">
            <p className="text-sm font-bold md:text-xl">
              {promoBannerSaleName}
            </p>
            <p className="text-[12px] font-semibold underline md:text-sm md:font-semibold">
              {promoBannerBoldText}
            </p>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-[12px] font-semibold sm:text-base sm:font-bold md:text-xl">
              {promoBannerTitle}
            </p>
            <p className="mt-cove-5 hidden text-[12px] font-semibold sm:block md:mt-0 md:text-sm md:font-semibold">
              {promoBannerText}
            </p>
          </div>
          <Formik
            initialValues={{ email: '' }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email('Please enter a valid address.')
                .required('Please enter a valid address.'),
            })}
            onSubmit={(values) => {
              handleSubscribe(values.email);
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              isValid,
              errors,
            }) => (
              <form
                onSubmit={handleSubmit}
                className="mt-cove-5 gap-cove-5 sm:mt-0"
              >
                {status === 'done' ? (
                  <p className="m-0 w-full text-center text-sm font-semibold not-italic max-md:text-left sm:text-base sm:font-semibold">
                    {t('layout.header.banner.offer-applied-at-checkout', {
                      defaultValue: 'Offer applied at checkout!',
                    })}
                  </p>
                ) : (
                  <div className="flex flex-row items-center gap-cove-15">
                    <div>
                      <TextInput
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        label={t('email-address', {
                          defaultValue: 'Email Address',
                        })}
                        size=""
                        inputWrapperCss={`
                        margin: 0;
                        @media (max-width: 600px) {
                          margin: 0
                          }  
                          `}
                        placeholder={t('email-address', {
                          defaultValue: 'Email Address',
                        })}
                        disabled={status === 'done'}
                        className="rounded-[10px] p-cove-15 placeholder:text-[12px] placeholder:font-normal placeholder:text-cove-navy-blue md:h-10"
                      />
                      <SmoothMount
                        show={!!errors.email || !!error?.length}
                        className="pt-cove-5 text-center text-xs font-medium not-italic text-cove-red sm:text-sm"
                      >
                        {errors.email || error}
                      </SmoothMount>
                      <SmoothMount
                        show={status === 'done'}
                        className="text-center text-xs font-medium not-italic"
                      >
                        {t('layout.header.banner.offer-applied-at-checkout', {
                          defaultValue: 'Offer applied at checkout!',
                        })}
                      </SmoothMount>
                    </div>
                    <Button
                      id={`banner-email-subscribe-${location.pathname}`}
                      cypressId="banner-email-subscribe"
                      type="submit"
                      variant="solid"
                      color="navy-blue"
                      disabled={!touched.email || !isValid || status === 'done'}
                      loading={status === 'submitting'}
                      className="h-[34px] min-h-0 w-[107px] min-w-0 shrink-0 justify-center px-cove-10 py-cove-5 text-base font-bold text-white"
                    >
                      {t('get-offer', { defaultValue: 'Get Offer' })}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    </SmoothMount>
  );
};

export default PromoBanner;
